<template>
  <va-modal
    :value="isOpen"
    ref="createOrUpdateRequestModal"
    :okDisabled="(!isUpdate && !didFindPharmacy) || isSubmitingForm"
    :okToClose="false"
    :noOutsideDismiss="true"
    :okText="$t('modals.buttons.save')"
    :cancelText="$t('modals.buttons.cancel')"
    :dangerText="$t('modals.buttons.delete')"
    :showDelete="isUpdate"
    @ok="submitForm"
    @cancel="closeModal"
    @remove="onClickDeleteEntityRequest"
  >

    <div slot="header" class="modal-title">
      <span v-if="!isUpdate">{{$t('entityrequests.create_request')}}</span>
      <span v-else>{{$t('entityrequests.edit_request')}}</span>
    </div>

    <form id="nif-search-form" class="evollu-form">
      <div class="row" v-if="!isUpdate">
          <h5 class="col-12">{{$t('entityrequests.pharmacy_search_title')}}</h5>
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
              <va-input
                id="nif-search-query"
                :placeholder="$t('entityrequests.inputs.nif_or_code')"
                v-model.trim="pharmacySearchQuery"
                :maxlength="10"
                @input="form.errors.clear('pharmacy_search')"
                :error="form.errors.has('pharmacy_search')"
                :error-messages="[$t(form.errors.get('pharmacy_search'))]"
              />
              <span class="w-25 px-3 clickable text-center">
                <va-button
                  @click.prevent="searchPharmacyByNIF"
                  :disabled="pharmacySearchQuery && isSearchingForPharmacy"
                >
                  <span>{{$t('buttons.search')}}</span>
                  <i class="ml-2 fa fa-search"></i>
                </va-button>
              </span>
            </div>
          </div>
      </div>
    </form>

    <form class="evollu-form py-3" v-if="isUpdate || didFindPharmacy">
      <div class="row">
        <h5 class="col-12">{{$t('entityrequests.entity_info')}}</h5>
      </div>

      <div class="row">
        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.entity_name')}}</label>
          <va-input
            id="input-entity-name"
            :placeholder="$t('entityrequests.inputs.entity_name')"
            v-model.trim="form.data.entity_name"
            :maxlength="255"
            @input="form.errors.clear('entity_name')"
            :error="form.errors.has('entity_name')"
            :error-messages="[$t(form.errors.get('entity_name'))]"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.entity_phone')}}</label>
          <va-input
            id="input-entity-phone"
            :placeholder="$t('entityrequests.inputs.entity_phone')"
            v-model.trim="form.data.entity_phone"
            :maxlength="20"
            @input="form.errors.clear('entity_phone')"
            :error="form.errors.has('entity_phone')"
            :error-messages="[$t(form.errors.get('entity_phone'))]"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.entity_nif')}}</label>
          <va-input
            id="input-entity-nif"
            :placeholder="$t('entityrequests.inputs.entity_nif')"
            v-model.trim="form.data.entity_nif"
            :maxlength="20"
            :disabled="didFindPharmacy && pharmacyDidHaveNif"
            @input="form.errors.clear('entity_nif')"
            :error="form.errors.has('entity_nif')"
            :error-messages="[$t(form.errors.get('entity_nif'))]"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.facility_code')}}</label>
          <va-input
            id="input-facility-code"
            :placeholder="$t('entityrequests.inputs.facility_code')"
            v-model.trim="form.data.facility_code"
            :maxlength="20"
            @input="form.errors.clear('facility_code')"
            :error="form.errors.has('facility_code')"
            :error-messages="[$t(form.errors.get('facility_code'))]"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.responsible_name')}}</label>
          <va-input
            id="input-entity-responsible-name"
            :placeholder="$t('entityrequests.inputs.responsible_name')"
            v-model.trim="form.data.responsible_name"
            :maxlength="255"
            @input="form.errors.clear('responsible_name')"
            :error="form.errors.has('responsible_name')"
            :error-messages="[$t(form.errors.get('responsible_name'))]"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.responsible_email')}}</label>
          <va-input
            id="input-entity-responsible-email"
            :placeholder="$t('entityrequests.inputs.responsible_email')"
            v-model.trim="form.data.responsible_email"
            :maxlength="255"
            @input="form.errors.clear('responsible_email')"
            :error="form.errors.has('responsible_email')"
            :error-messages="[$t(form.errors.get('responsible_email'))]"
          />
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.contact_name')}}</label>
          <va-input
            id="input-entity-contact-name"
            :placeholder="$t('entityrequests.inputs.contact_name')"
            v-model.trim="form.data.contact_name"
            :maxlength="255"
            @input="form.errors.clear('contact_name')"
            :error="form.errors.has('contact_name')"
            :error-messages="[$t(form.errors.get('contact_name'))]"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{$t('entityrequests.inputs.contact_email')}}</label>
          <va-input
            id="input-entity-contact-email"
            :placeholder="$t('entityrequests.inputs.contact_email')"
            v-model.trim="form.data.contact_email"
            :maxlength="255"
            @input="form.errors.clear('contact_email')"
            :error="form.errors.has('contact_email')"
            :error-messages="[$t(form.errors.get('contact_email'))]"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <label class="form-label">{{$t('entityrequests.senior_manager')}}</label>
          <multiselect
            v-model="form.data.senior_manager"
            :options="seniorManagers"
            track-by="id"
            label="name"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :disabled="canSetResponsible('Senior Manager')"
            @input="form.errors.clear('senior_manager')"
            :searchable="true"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
          </multiselect>
          <small v-show="form.errors.has('senior_manager')" class="help text-danger">
            {{ $t(form.errors.get('senior_manager')) }}
          </small>
        </div>

        <div class="col-12 mb-3">
          <label class="form-label">{{$t('entityrequests.manager')}}</label>
          <multiselect
            v-model="form.data.manager"
            :options="managers"
            track-by="id"
            label="name"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            :allow-empty="true"
            :multiple="false"
            :disabled="canSetResponsible('Manager')"
            :searchable="true"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
          </multiselect>
          <small v-show="form.errors.has('manager')" class="help text-danger">
            {{ $t(form.errors.get('manager')) }}
          </small>
        </div>
        <div class="col-12 mb-3">
          <label class="form-label">{{$t('entityrequests.consultant')}}</label>
          <multiselect
            v-model="form.data.consultant"
            :options="consultants"
            track-by="id"
            label="name"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            :allow-empty="true"
            :multiple="false"
            :searchable="true"
            :disabled="canSetResponsible('Consultant')"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
          </multiselect>
          <small v-show="form.errors.has('consultant')" class="help text-danger">
            {{ $t(form.errors.get('consultant')) }}
          </small>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <label class="form-label">{{$t('entityrequests.preferred_week_day')}}</label>
          <multiselect
            v-model="form.data.preferred_week_day"
            :options="weekDays"
            track-by="value"
            :custom-label="option => $t(option.name)"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            :allow-empty="true"
            :multiple="false"
            :searchable="true"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>

            <template slot="option" slot-scope="{option}">
              {{ $t(option.name) }}
            </template>

            <template slot="singleLabel" slot-scope="{option}">
              {{ $t(option.name) }}
            </template>

            <span slot="option">{{$t('patients.noResults')}}</span>
          </multiselect>
        </div>

        <div class="col-12 mb-3">
          <label class="form-label">{{$t('entityrequests.business_plans')}}</label>
          <multiselect
            v-model="form.data.business_plan"
            :options="businessPlans"
            track-by="id"
            label="name"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            @input="form.errors.clear('business_plan')"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
          </multiselect>

          <small v-show="form.errors.has('business_plan')" class="help text-danger">
            {{ $t(form.errors.get('business_plan')) }}
          </small>
        </div>

        <div v-if="form.data.business_plan !== null" class="col-12 px-2" v-html="form.data.business_plan.html_description">

        </div>
      </div>

      <div class="row">
        <div class="col-12" v-if="form.data.business_plan == null || form.data.business_plan && form.data.business_plan.id !== businessPlans[businessPlans.length -1].id">
          <va-checkbox
            :label="$t('entityrequests.inputs.premium_pack_interest')"
            :id="'checkBoxPremiumPackInterest'"
            :isCircle="true"
            v-model="form.data.has_premium_pack_interest"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <va-checkbox
            :label="$t('entityrequests.already_has_previous_aud_provider_question')"
            id="checkBoxPreviousAudProvider"
            :isCircle="true"
            v-model="form.data.had_previous_aud_provider"
          />
        </div>

        <div class="col-12" v-if="form.data.had_previous_aud_provider">
          <label class="form-label">{{$t('entityrequests.inputs.tell_us_your_experience')}}</label>
          <va-input
            id="input-entity-had-previous-aud-provider"
            type="textarea"
            :placeholder="$t('entityrequests.inputs.previous_aud_provider')"
            v-model.trim="form.data.previous_aud_provider"
            :maxlength="500"
            :rows="3"
            @input="form.errors.clear('previous_aud_provider')"
            :error="form.errors.has('previous_aud_provider')"
            :error-messages="[$t(form.errors.get('previous_aud_provider'))]"
          />
        </div>

        <div class="col-12" v-if="form.data.observations">
          <label class="form-label">{{$t('entityrequests.inputs.observations')}}</label>
          <va-input
            id="input-entity-observations"
            type="textarea"
            :placeholder="$t('entityrequests.inputs.observations')"
            v-model.trim="form.data.observations"
            :maxlength="500"
            :rows="3"
            @input="form.errors.clear('observations')"
            :error="form.errors.has('observations')"
            :error-messages="[$t(form.errors.get('observations'))]"
          />
        </div>
      </div>

       <address-form-input
          :label="$t('entityrequests.address')"
          :newPlace="!isUpdate"
          :resource="{name: form.data.entity_name}"
          v-model="form.data.address"
        >
          <div slot="otherFields" slot-scope="{ country }">
            <div v-if="country.code === 'PT'">
              <div class="row">
                <div class="col-12">
                  <label class="form-label">{{$t('patients.inputs.address.distrito')}}</label>
                  <multiselect
                    v-model="form.data.address_district"
                    :options="districts"
                    track-by="id"
                    label="name"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    placeholder="---"
                    :allow-empty="false"
                    :multiple="false"
                    :searchable="true"
                    @input="form.errors.clear('address_district')"
                    >
                    <span slot="noResult">{{$t('patients.noResults')}}</span>
                  </multiselect>
                  <small v-show="form.errors.has('address_district')" class="help text-danger">
                    {{ $t(form.errors.get('address_district')) }}
                  </small>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <label class="form-label">{{$t('patients.inputs.address.concelho')}}</label>
                  <multiselect
                    v-model="form.data.address_council"
                    :options="councils"
                    track-by="id"
                    label="name"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    placeholder="---"
                    :allow-empty="true"
                    :multiple="false"
                    :searchable="true"
                    :disabled="form.data.address_district == null"
                    @input="form.errors.clear('address_council')"
                    >
                    <span slot="noResult">{{$t('patients.noResults')}}</span>
                  </multiselect>
                  <small v-show="form.errors.has('address_council')" class="help text-danger">
                    {{ $t(form.errors.get('address_council')) }}
                  </small>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <label class="form-label">{{$t('patients.inputs.address.freguesia')}}</label>
                  <multiselect
                    v-model="form.data.address_parish"
                    :options="parishes"
                    track-by="id"
                    label="name"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    placeholder="---"
                    :allow-empty="true"
                    :multiple="false"
                    :searchable="true"
                    :disabled="form.data.address_council == null"
                    >
                    <span slot="noResult">{{$t('patients.noResults')}}</span>
                  </multiselect>
                </div>
              </div>

            </div>
          </div>
        </address-form-input>
    </form>
  </va-modal>
</template>
<script>
import Errors from '@/utils/StandardErrors'
import { EntityRequestService, GlinttHierarchyService } from '@/api/LeadsService'
import { ZonesService } from '@/api/AuthService'
import Multiselect from 'vue-multiselect'
import AddressFormInput from '@/components/patients/AddressFormInput'

const PROJECT_MANAGER_EVOLLU_ID = 1
const PROJECT_MANAGER_ID = 1
const SENIOR_MANAGER_ID = 3
const MANAGER_ID = 4
const CONSULTANT_ID = 5

export default {
  props: {
    entityRequest: {
      type: Object,
      required: false
    },
    businessPlans: {
      type: Array,
      required: true
    },
    userProfile: {
      type: Object,
      required: true
    },
    weekDays: {
      type: Array,
      required: true
    }
  },
  components: {
    Multiselect,
    AddressFormInput
  },
  computed: {
    isUpdate: function () {
      return this.entityRequest !== null && this.entityRequest !== undefined
    },
    responsibleEmailIsInvalid: function () {
      if (this.form.data.responsible_email) {
        return !this.form.data.responsible_email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)
      }
      return true
    },
    contactEmailIsInvalid: function () {
      if (this.form.data.contact_email) {
        return !this.form.data.contact_email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)
      }
      return true
    },
    entityPhoneIsInvalid: function () {
      if (this.form.data.entity_phone) {
        return !this.form.data.entity_phone.match(/[9,2]\d{8}/)
      }
      return false
    }
  },
  data: function () {
    return {
      isOpen: false,
      pharmacySearchQuery: '',
      didFindPharmacy: false,
      pharmacyDidHaveNif: false,
      isSearchingForPharmacy: false,
      form: {
        data: {
          entity_name: '',
          entity_nif: '',
          entity_phone: null,
          facility_code: null,
          responsible_name: '',
          responsible_email: '',
          contact_name: '',
          contact_email: '',
          manager: null,
          senior_manager: null,
          consultant: null,
          kiosk_interest: false,
          kiosk_pricing: false,
          portable_pricing: false,
          observations: '',
          address: {
            line1: '',
            line2: '',
            city: '',
            postal_code: '',
            country: undefined,
            lat: undefined,
            lng: undefined
          },
          address_district: null,
          address_council: null,
          address_parish: null,
          business_plan: null,
          has_premium_pack_interest: false,
          preferred_week_day: null,
          had_previous_aud_provider: false,
          previous_aud_provider: null
        },
        errors: new Errors()
      },
      districts: [],
      councils: [],
      parishes: [],
      seniorManagers: [],
      managers: [],
      consultants: [],
      isSubmitingForm: false
    }
  },
  created: async function () {
    ZonesService.getPortugueseDistricts()
      .then(districts => {
        this.districts = districts
        if (this.isUpdate) {
          this.prepareForUpdate(this.entityRequest)
        }
      })
  },
  mounted: function () {
    this.isOpen = true
  },
  methods: {
    onClickDeleteEntityRequest: function () {
      this.$emit('deleteEntityRequest', this.entityRequest)
      setTimeout(() => { this.isOpen = false }, 500)
    },
    searchPharmacyByNIF: function () {
      if (this.pharmacySearchQuery && !this.isSearchingForPharmacy) {
        this.didFindPharmacy = false
        this.isSearchingForPharmacy = true
        this.form.errors.clear()
        EntityRequestService.getPharmacyByQuery(this.pharmacySearchQuery)
          .then(pharmacy => {
            this.prepareFormWithPharmacyData(pharmacy)
            this.didFindPharmacy = true
          })
          .catch(errors => {
            if (errors.response && errors.response.status === 404) {
              this.form.errors.set('pharmacy_search', 'entityrequests.errors.could_not_find_pharmacy')
            } else {
              this.showToast(this.$t('entityrequests.errors.something_went_wrong'), this.$store.state.errorToastOptions)
            }

            this.didFindPharmacy = false
          })
          .finally(() => {
            this.isSearchingForPharmacy = false
          })
      }
    },
    prepareFormWithPharmacyData: async function (pharmacy) {
      try {
        this.form.data.facility_code = `ANF ${pharmacy.cod_anf.trim()}`
        this.form.data.entity_phone = ''
        this.form.data.entity_name = pharmacy.nome
        this.form.data.entity_nif = pharmacy.nipc
        this.form.data.address.line1 = pharmacy.rua
        this.form.data.address.line2 = pharmacy.numero
        this.form.data.address.city = pharmacy.localidade
        this.form.data.address.line2 = pharmacy.numero
        this.form.data.address.postal_code = pharmacy.cp_zona.trim() + '-' + pharmacy.cp_sufixo.trim() + ' ' + pharmacy.localidadepostal.trim()
        this.form.data.address_district = this.districts.find(district => district.name === pharmacy.distrito.trim()) || null

        if (this.form.data.address_district) {
          this.councils = await ZonesService.getPortugueseCouncils(this.form.data.address_district.id)
          this.form.data.address_council = this.councils.find(council => council.name === pharmacy.concelho.trim())

          if (this.form.data.address_council) {
            this.parishes = await ZonesService.getPortugueseParishes(this.form.data.address_council.id)
            this.form.data.address_parish = this.parishes.find(parish => parish.name === pharmacy.freguesia.trim())
          }
        }

        if (this.form.data.entity_nif && this.form.data.entity_nif.length > 0) {
          this.pharmacyDidHaveNif = true
        }
      } catch (e) {
        console.log(e)
        // TODO: launch error
      }
    },
    prepareForUpdate: async function (entityRequest) {
      try {
        this.form.data.entity_name = entityRequest.entity_name || ''
        this.form.data.entity_nif = entityRequest.entity_nif || ''
        this.form.data.facility_code = entityRequest.facility_code || ''
        this.form.data.entity_phone = entityRequest.entity_phone || ''
        this.form.data.responsible_name = entityRequest.responsible_name || ''
        this.form.data.responsible_email = entityRequest.responsible_email || ''
        this.form.data.contact_name = entityRequest.contact_name || ''
        this.form.data.contact_email = entityRequest.contact_email || ''
        this.form.data.address.line1 = entityRequest.address_line_1 || null
        this.form.data.address.line2 = entityRequest.address_line_2 || null
        this.form.data.address.city = entityRequest.address_city || null
        this.form.data.address.postal_code = entityRequest.address_postal_code || null
        this.form.data.address.country = entityRequest.address_country || null
        this.form.data.address.lat = entityRequest.address_lat || null
        this.form.data.address.lng = entityRequest.address_lng || null
        this.form.data.senior_manager = entityRequest.senior_manager || null
        this.form.data.manager = entityRequest.manager || null
        this.form.data.consultant = entityRequest.consultant || null
        this.form.data.observations = entityRequest.observations || ''

        console.log(entityRequest.manager)

        this.form.data.kiosk_interest = entityRequest.kiosk_interest
        this.form.data.kiosk_pricing = false
        this.form.data.portable_pricing = false
        this.form.data.preferred_week_day = this.weekDays.find(weekDays => weekDays.value === entityRequest.preferred_week_day)
        this.form.data.business_plan = this.businessPlans.find(businessPlan => businessPlan.id === entityRequest.business_plan_id) || null
        this.form.data.has_premium_pack_interest = entityRequest.has_premium_pack_interest
        this.form.data.had_previous_aud_provider = entityRequest.previous_aud_provider !== null
        this.form.data.previous_aud_provider = entityRequest.previous_aud_provider || ''

        let zoneCode = entityRequest.address_zone_code.toString()
        let districtCode = null
        let councilCode = null
        let parishCode = null

        if (zoneCode.length === 5 || zoneCode.length === 3) {
          districtCode = `0${zoneCode.substring(0, 1)}`
          councilCode = `0${zoneCode.substring(0, 3)}`
          if (zoneCode.length === 5) {
            parishCode = `0${zoneCode}`
          }
        } else {
          districtCode = zoneCode.substring(0, 2)
          councilCode = zoneCode.substring(0, 4)
          if (zoneCode.length === 6) {
            parishCode = zoneCode
          }
        }

        console.log(zoneCode, typeof districtCode, councilCode, parishCode)

        this.form.data.address_district = this.districts.find(distrito => distrito.id === districtCode)

        if (this.form.data.address_district) {
          this.councils = await ZonesService.getPortugueseCouncils(this.form.data.address_district.id)
          this.form.data.address_council = this.councils.find(council => council.id === councilCode)

          if (this.form.data.address_council) {
            this.parishes = await ZonesService.getPortugueseParishes(this.form.data.address_council.id)
            this.form.data.address_parish = this.parishes.find(parish => parish.id === parishCode)
          }
        }
      } catch (e) {
        console.log(e)
        // TODO: launch error
      }
    },
    validateForm: function () {
      // 2030 TODO: Change the validations to server side
      if (!this.form.data.entity_name) {
        this.form.errors.set('entity_name', 'entityrequests.errors.required_entity_name')
      }

      if (!this.form.data.entity_nif) {
        this.form.errors.set('entity_nif', 'entityrequests.errors.required_entity_nif')
      }

      if (!this.form.data.entity_phone || this.entityPhoneIsInvalid) {
        this.form.errors.set('entity_phone', 'entityrequests.errors.required_entity_phone')
      }

      if (!this.form.data.responsible_name) {
        this.form.errors.set('responsible_name', 'entityrequests.errors.required_person_name')
      }

      if (!this.form.data.responsible_email || this.responsibleEmailIsInvalid) {
        this.form.errors.set('responsible_email', 'entityrequests.errors.invalid_email')
      }

      if ((this.form.data.contact_name && this.form.data.contact_name.length > 0) || (this.form.data.contact_email && this.form.data.contact_email.length > 0)) {
        if (!this.form.data.contact_name) {
          this.form.errors.set('contact_name', 'entityrequests.errors.invalid_contact_name')
        }

        if (!this.form.data.contact_email || this.contactEmailIsInvalid) {
          this.form.errors.set('contact_email', 'entityrequests.errors.invalid_contact_email')
        }
      }

      if (!this.form.data.address_district) {
        this.form.errors.set('address_district', 'entityrequests.errors.required_address_zone')
      }

      if (!this.form.data.address_council) {
        this.form.errors.set('address_council', 'entityrequests.errors.required_address_zone')
      }

      if (!this.form.data.senior_manager) {
        this.form.errors.set('senior_manager', 'entityrequests.errors.senior_manager_is_required')
      }

      if (!this.form.data.business_plan) {
        this.form.errors.set('business_plan', 'entityrequests.errors.business_plan_is_required')
      }
    },
    getFormData: function () {
      let data = JSON.parse(JSON.stringify(this.form.data))

      if (!data.contact_name || !data.contact_email) {
        delete data.contact_name
        delete data.contact_email
      }

      if (data.senior_manager) {
        data.senior_manager_id = data.senior_manager.id
      }

      delete data.senior_manager

      if (data.manager) {
        data.manager_id = data.manager.id
      }

      delete data.manager

      if (data.consultant) {
        data.consultant_id = data.consultant.id
      }

      delete data.consultant

      if (data.preferred_week_day) {
        data.preferred_week_day = data.preferred_week_day.value
      }

      if (data.business_plan) {
        data.business_plan_id = data.business_plan.id
      }
      delete data.business_plan

      if (data.address) {
        if (data.address.line1) {
          data.address_line_1 = data.address.line1
        }

        if (data.address.line2) {
          data.address_line_2 = data.address.line2
        }

        if (data.address.city) {
          data.address_city = data.address.city
        }

        if (data.address.postal_code) {
          data.address_postal_code = data.address.postal_code
        }

        if (data.address.country) {
          data.address_country = data.address.country
        }

        if (data.address.lat) {
          data.address_lat = data.address.lat
        }

        if (data.address.lng) {
          data.address_lng = data.address.lng
        }
      }

      if (data.address_parish) {
        data.address_zone_code = data.address_parish.id
      } else if (data.address_council) {
        data.address_zone_code = data.address_council.id
      }

      delete data.address_district
      delete data.address_council
      delete data.address_parish

      return data
    },
    submitForm: function () {
      if (!this.isSubmitingForm) {
        this.isSubmitingForm = true
        this.validateForm()

        if (this.form.errors.any()) {
          this.showToast(this.$t('entityrequests.errors.form_contains_errors'), this.$store.state.errorToastOptions)
          this.isSubmitingForm = false
          return
        }

        const data = this.getFormData()
        const request = this.isUpdate ? EntityRequestService.update(this.entityRequest.id, data) : EntityRequestService.store(data)

        request
          .then(entityRequest => {
            if (this.isUpdate) {
              this.$emit('entity-request-updated', { entityRequest })
              this.showToast(this.$t('entityrequests.request_updated'), this.$store.state.successToastOptions)
            } else {
              this.$emit('entity-request-created', { entityRequest })
              this.showToast(this.$t('entityrequests.request_created'), this.$store.state.successToastOptions)
            }
            this.$refs.createOrUpdateRequestModal.cancel()
          })
          .catch(errors => {
            console.log(errors)
            this.showToast(this.$t('leads.messages.error'), this.$store.state.errorToastOptions)
          })
          .finally(() => {
            this.isSubmitingForm = false
          })
      }
    },
    canSetResponsible (profile) {
      let profiles = ['Project Manager Evollu', 'Project Manager', 'Senior Manager', 'Manager', 'Consultant']
      let userProfile = this.userProfile.profile.type
      return profiles.indexOf(profile) <= profiles.indexOf(userProfile)
    },
    /*
    canSetResponsible: function (profileId) {
      return profileId - this.userProfile.profile.id >= 0
    }, */
    closeModal: function () {
      this.isOpen = false
      setTimeout(() => this.$emit('cancel'), 500)
    }
  },
  watch: {
    'form.data.address_district': function (value) {
      this.form.data.address_council = null
      this.form.data.address_parish = null
      this.parishes = []
      if (value !== null) {
        ZonesService.getPortugueseCouncils(value.id)
          .then(councils => {
            this.councils = councils
          })
          .catch(errors => console.log(errors))
      } else {
        this.councils = []
        this.parishes = []
      }
    },
    'form.data.address_council': function (value) {
      this.form.data.address_parish = null
      if (value !== null) {
        ZonesService.getPortugueseParishes(value.id)
          .then(parishes => {
            this.parishes = parishes
          })
          .catch(errors => console.log(errors))
      } else {
        this.parishes = []
      }
    },
    'userProfile': {
      handler: function (userProfile) {
        if (userProfile !== null) {
          switch (userProfile.profile.profile_id) {
          case SENIOR_MANAGER_ID:
            this.form.data.senior_manager = userProfile.profile
            if (userProfile.parent) {
              // this.editingProjectManager = this.userProfile.parent.profile
            }
            break

          case MANAGER_ID:
            this.form.data.manager = userProfile.profile
            if (userProfile.parent) {
              this.form.data.senior_manager = userProfile.parent.profile
            }
            break

          case CONSULTANT_ID:
            this.form.data.consultant = this.userProfile.profile
            if (userProfile.parent) {
              this.form.data.manager = userProfile.parent.profile
            }
            if (userProfile.parent.parent) {
              this.form.data.senior_manager = userProfile.parent.parent.profile
            }
            break

          case PROJECT_MANAGER_EVOLLU_ID:
          case PROJECT_MANAGER_ID:
            this.seniorManagers = this.userProfile.childrens
            break
          }
        }
      },
      immediate: true
    },
    'form.data.senior_manager': function (seniorManager) {
      if (seniorManager !== null) {
        GlinttHierarchyService.getUserProfile(seniorManager.id, { childs: true })
          .then(managers => {
            console.log(managers)
            this.managers = managers
          })
          .catch(errors => {
            console.log(errors)
            this.managers = []
            this.consultants = []
          })
      } else {
        this.managers = []
        this.consultants = []
      }
    },
    'form.data.manager': function (manager) {
      if (manager !== null) {
        GlinttHierarchyService.getUserProfile(manager.id, { childs: true })
          .then(consultants => {
            this.consultants = consultants
          })
          .catch(errors => {
            console.log(errors)
            this.consultants = []
          })
      } else {
        this.consultants = []
      }
    },
    'seniorManagers': function (seniorManagers) {
      if (this.form.data.senior_manager !== null && !seniorManagers.some(seniorManagers => seniorManagers.id === this.form.data.senior_manager.id)) {
        this.form.data.senior_manager = null
      }
    },
    'managers': function (managers) {
      if (this.form.data.manager !== null && !managers.some(manager => manager.id === this.form.data.manager.id)) {
        this.form.data.manager = null
      }

      if (managers.length === 1) {
        this.form.data.manager = managers[0]
      }
    },
    'consultants': function (consultants) {
      if (this.form.data.consultant !== null && !consultants.some(consultant => consultant.id === this.form.data.consultant.id)) {
        this.form.data.consultant = null
      }

      if (consultants.length === 1) {
        this.form.data.consultant = consultants[0]
      }
    }
  }
}
</script>
